/**
 * @fileoverview 乗下船記録
 * @description 現在乗船中の一覧と、乗組員ごとの乗下船履歴（船員手帳記載用）を管理する。
 */
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { get, getToken } from '../services/api';
import * as api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { DateRangeFilter, inDateRange } from '../components/DateRangeFilter';

type Tab = 'current' | 'history';

interface CrewUser { id: string; user_name: string; user_role: string; vessel_name?: string | null }
interface Vessel { id: string; vessel_name: string }

// 乗組員を船ごとにまとめた検索用オプション（船名→氏名でソート。未配属は末尾）
function crewVesselOptions(crew: CrewUser[]): { value: string; label: string; group: string }[] {
  const UNASSIGNED = '未配属';
  return crew
    .map(c => ({ value: c.id, label: c.user_name, group: c.vessel_name || UNASSIGNED }))
    .sort((a, b) => {
      if (a.group !== b.group) {
        if (a.group === UNASSIGNED) return 1;
        if (b.group === UNASSIGNED) return -1;
        return a.group.localeCompare(b.group, 'ja');
      }
      return a.label.localeCompare(b.label, 'ja');
    });
}
interface Port { id: string; name: string }
interface CurrentRow {
  user_id: string; user_name: string; vessel_id: string; vessel_name: string;
  boarded_at: string | null; position: string | null; boarding_days: number | null;
}
interface BoardingRecord {
  id: string; user_id: string; user_name?: string; vessel_id: string; vessel_name: string;
  record_type: string; recorded_at: string; port_name: string | null; position: string | null; note: string | null;
}
interface Period {
  vessel_name: string; boarded_at: string | null; departed_at: string | null;
  days: number | null; position: string | null;
}

const fmtDate = (v?: string | null) => (v ? String(v).slice(0, 10) : '-');
const fmtDateTime = (v?: string | null) => (v ? new Date(v).toLocaleString('ja-JP') : '-');

function TypeBadge({ type }: { type: string }) {
  const board = type === 'board';
  const m = board
    ? { label: '乗船', bg: '#f0fdf4', fg: '#16a34a', border: '#bbf7d0' }
    : { label: '下船', bg: '#f1f5f9', fg: '#64748b', border: '#e2e8f0' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: m.bg, color: m.fg, border: `1px solid ${m.border}` }}>
      {m.label}
    </span>
  );
}

const BoardingPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('current');
  const [crew, setCrew] = useState<CrewUser[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [ports, setPorts] = useState<Port[]>([]);

  // 乗船中タブ
  const [current, setCurrent] = useState<CurrentRow[]>([]);
  const [curLoading, setCurLoading] = useState(true);
  const [curUserId, setCurUserId] = useState('');
  const [showRecordModal, setShowRecordModal] = useState(false);

  // 履歴タブ
  const [historyUserId, setHistoryUserId] = useState('');
  const [records, setRecords] = useState<BoardingRecord[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [histFrom, setHistFrom] = useState('');
  const [histTo, setHistTo] = useState('');
  const [histLoading, setHistLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, v] = await Promise.all([
          get<any>('/users', { limit: '500' }),
          get<any>('/vessels', {}),
        ]);
        setCrew((u.users ?? u.data ?? []) as CrewUser[]);
        setVessels((v.vessels ?? v.data ?? []) as Vessel[]);
      } catch (e) { console.error('[BoardingPage/masters]', e); }
      try {
        const p = await get<any>('/port-masters', {});
        setPorts((p.data ?? p.ports ?? []) as Port[]);
      } catch { /* 港マスタが無い環境ではテキスト入力にフォールバック */ }
    })();
  }, []);

  const loadCurrent = useCallback(async () => {
    setCurLoading(true);
    try {
      const res = await get<any>('/boarding/current', {});
      setCurrent((res.data ?? []) as CurrentRow[]);
    } catch (e) { console.error('[BoardingPage/current]', e); }
    setCurLoading(false);
  }, []);

  const loadHistory = useCallback(async (uid: string) => {
    if (!uid) { setRecords([]); setPeriods([]); return; }
    setHistLoading(true);
    try {
      const res = await get<any>('/boarding/history/' + uid, {});
      const data = res.data ?? {};
      setRecords((data.records ?? []) as BoardingRecord[]);
      setPeriods((data.periods ?? []) as Period[]);
    } catch (e) { console.error('[BoardingPage/history]', e); }
    setHistLoading(false);
  }, []);

  useEffect(() => { if (tab === 'current') loadCurrent(); }, [tab, loadCurrent]);
  useEffect(() => { if (tab === 'history') loadHistory(historyUserId); }, [tab, historyUserId, loadHistory]);

  const downloadPdf = async () => {
    if (!historyUserId) return;
    setPdfLoading(true);
    try {
      const subdomain = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
      const token = getToken() ?? '';
      const r = await fetch(`${apiBase}/boarding/pdf?user_id=${historyUserId}`, { headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' } });
      if (!r.ok) throw new Error('PDF生成に失敗しました');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = '乗船履歴.pdf';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) { alert(e.message); }
    setPdfLoading(false);
  };

  const tabBtn = (t: Tab, label: string) => (
    <button onClick={() => setTab(t)} style={{
      padding: '7px 18px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700,
      background: tab === t ? '#fff' : 'transparent',
      color: tab === t ? '#0f172a' : '#64748b',
      boxShadow: tab === t ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
    }}>{label}</button>
  );

  // 乗船中: 選択した乗組員で絞り込み（未選択なら全件）
  const filteredCurrent = current.filter(c => !curUserId || c.user_id === curUserId);
  // 乗船中の検索候補（現在乗船中の乗組員を船ごとにまとめる）
  const currentCrewOptions = [
    { value: '', label: 'すべて（全船・全乗組員）' },
    ...current
      .map(c => ({ value: c.user_id, label: c.user_name, group: c.vessel_name || '(船舶未設定)' }))
      .sort((a, b) => a.group !== b.group ? a.group.localeCompare(b.group, 'ja') : a.label.localeCompare(b.label, 'ja')),
  ];

  // 乗船中: 船ごとにグループ化（船名順、船内は乗船日数の多い順）
  const currentByVessel = (() => {
    const map = new Map<string, { vessel_name: string; rows: CurrentRow[] }>();
    for (const c of filteredCurrent) {
      const key = c.vessel_id || c.vessel_name || '__none';
      if (!map.has(key)) map.set(key, { vessel_name: c.vessel_name || '(船舶未設定)', rows: [] });
      map.get(key)!.rows.push(c);
    }
    const groups = [...map.values()];
    groups.forEach(g => g.rows.sort((a, b) => (b.boarding_days ?? 0) - (a.boarding_days ?? 0)));
    groups.sort((a, b) => a.vessel_name.localeCompare(b.vessel_name, 'ja'));
    return groups;
  })();

  // 履歴: 乗船期間は指定期間との重なり、イベントは発生日時で絞り込み
  const filteredPeriods = periods.filter(p => {
    if (histTo   && p.boarded_at && fmtDate(p.boarded_at) > histTo) return false;
    if (histFrom && p.departed_at && fmtDate(p.departed_at) < histFrom) return false;
    return true;
  });
  const timeline = records
    .filter(r => inDateRange(String(r.recorded_at ?? ''), histFrom, histTo))
    .sort((a, b) => String(b.recorded_at).localeCompare(String(a.recorded_at)));

  // 履歴の乗船期間: 船ごとにグループ化（船名順、船内は乗船日の新しい順）
  const periodsByVessel = (() => {
    const map = new Map<string, { vessel_name: string; rows: Period[]; totalDays: number }>();
    for (const p of filteredPeriods) {
      const key = p.vessel_name || '(船舶未設定)';
      if (!map.has(key)) map.set(key, { vessel_name: key, rows: [], totalDays: 0 });
      const g = map.get(key)!;
      g.rows.push(p);
      g.totalDays += p.days ?? 0;
    }
    const groups = [...map.values()];
    groups.forEach(g => g.rows.sort((a, b) => String(b.boarded_at ?? '').localeCompare(String(a.boarded_at ?? ''))));
    groups.sort((a, b) => a.vessel_name.localeCompare(b.vessel_name, 'ja'));
    return groups;
  })();

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">乗下船記録</h1>
        <div className="flex-row">
          {tab === 'current' && <button className="btn-p" onClick={() => setShowRecordModal(true)}>乗下船を記録</button>}
          {tab === 'history' && (
            <button className="btn-o" style={{ background: '#dc2626', color: '#fff', borderColor: '#dc2626' }}
              disabled={!historyUserId || pdfLoading} onClick={downloadPdf}>
              {pdfLoading ? '生成中…' : '船員手帳用PDF'}
            </button>
          )}
        </div>
      </div>
      {/* この画面の役割と反映タイミング（配乗シミュレーション／乗下船表／乗下船記録の使い分け） */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#1e3a8a' }}>
        <span><strong>この画面:</strong> 実際に乗った・降りた記録（船員手帳の元）です。ここで記録するとその場で乗船中の状態に反映されます。船員アプリの自己乗下船もここに残ります。</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 12 }}><Link to="/crew-sim" style={{ fontWeight: 800, color: '#1d4ed8', textDecoration: 'none' }}>配乗シミュレーション →</Link><Link to="/crew-assign" style={{ fontWeight: 800, color: '#1d4ed8', textDecoration: 'none' }}>乗下船表 →</Link></span>
      </div>

      <div style={{ display: 'inline-flex', background: '#F1F5F9', borderRadius: 10, padding: 3, gap: 2, marginBottom: 20 }}>
        {tabBtn('current', '乗船中')}
        {tabBtn('history', '履歴')}
      </div>

      {tab === 'current' && (
        curLoading ? <div className="loading"><div className="spinner" /></div> : (
          <>
          <div className="flex-row" style={{ marginBottom: 16, alignItems: 'flex-end' }}>
            <div className="form-g" style={{ margin: 0, minWidth: 260 }}>
              <label className="form-l">乗組員で絞り込み</label>
              <SearchableSelect
                value={curUserId}
                onChange={setCurUserId}
                options={currentCrewOptions}
                placeholder="クリックで一覧・氏名で検索..."
              />
            </div>
            <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 12, paddingBottom: 6 }}>
              {currentByVessel.length}隻 / {filteredCurrent.length}名
            </span>
          </div>
          {currentByVessel.length === 0 ? (
            <div className="card" style={{ padding: 0 }}><table><tbody>
              <tr><td className="empty">現在乗船中の乗組員はいません</td></tr>
            </tbody></table></div>
          ) : currentByVessel.map(g => (
            <div key={g.vessel_name} className="card" style={{ padding: 0, overflow: 'auto', marginBottom: 16 }}>
              <div className="card-h" style={{ padding: '14px 24px 0', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>🚢 {g.vessel_name}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>{g.rows.length}名 乗船中</span>
              </div>
              <table>
                <thead>
                  <tr><th>乗組員</th><th>乗船日時</th><th>役職</th><th>乗船日数</th></tr>
                </thead>
                <tbody>
                  {g.rows.map(c => (
                    <tr key={c.user_id + c.vessel_id}>
                      <td style={{ fontWeight: 600 }}>{c.user_name}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(c.boarded_at)}</td>
                      <td>{c.position || '-'}</td>
                      <td style={{ fontWeight: 700 }}>{c.boarding_days != null ? `${c.boarding_days}日` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          </>
        )
      )}

      {tab === 'history' && (
        <>
          <div className="flex-row" style={{ marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
            <div className="form-g" style={{ margin: 0, minWidth: 240 }}>
              <label className="form-l">乗組員</label>
              <SearchableSelect
                value={historyUserId}
                onChange={setHistoryUserId}
                options={crewVesselOptions(crew)}
                placeholder="氏名で検索..."
              />
            </div>
            {historyUserId && (
              <div style={{ paddingBottom: 2 }}>
                <DateRangeFilter from={histFrom} to={histTo} onFrom={setHistFrom} onTo={setHistTo} />
              </div>
            )}
          </div>

          {!historyUserId ? (
            <div className="empty">乗組員を選択すると乗下船履歴を表示します</div>
          ) : histLoading ? <div className="loading"><div className="spinner" /></div> : (
            <>
              <div className="card-h" style={{ margin: '0 0 10px' }}>乗船期間（船ごと）</div>
              {periodsByVessel.length === 0 ? (
                <div className="card" style={{ padding: 0 }}><table><tbody>
                  <tr><td className="empty">乗船期間の記録がありません</td></tr>
                </tbody></table></div>
              ) : periodsByVessel.map(g => (
                <div key={g.vessel_name} className="card" style={{ padding: 0, overflow: 'auto', marginBottom: 16 }}>
                  <div className="card-h" style={{ padding: '14px 24px 0', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>🚢 {g.vessel_name}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>{g.rows.length}回 / 通算 {g.totalDays}日</span>
                  </div>
                  <table>
                    <thead>
                      <tr><th>乗船日</th><th>下船日</th><th>乗船日数</th><th>役職</th></tr>
                    </thead>
                    <tbody>
                      {g.rows.map((p, i) => (
                        <tr key={i}>
                          <td>{fmtDate(p.boarded_at)}</td>
                          <td>{p.departed_at ? fmtDate(p.departed_at) : <span style={{ color: '#16a34a', fontWeight: 700 }}>乗船中</span>}</td>
                          <td style={{ fontWeight: 700 }}>{p.days != null ? `${p.days}日` : '-'}</td>
                          <td>{p.position || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              <div className="card" style={{ padding: 0, overflow: 'auto' }}>
                <div className="card-h" style={{ padding: '14px 24px 0', margin: 0 }}>乗下船イベント（時系列）</div>
                <table>
                  <thead>
                    <tr><th>日時</th><th>種別</th><th>船舶</th><th>港</th><th>役職</th><th>メモ</th></tr>
                  </thead>
                  <tbody>
                    {timeline.length === 0 ? (
                      <tr><td colSpan={6} className="empty">記録がありません</td></tr>
                    ) : timeline.map(r => (
                      <tr key={r.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.recorded_at)}</td>
                        <td><TypeBadge type={r.record_type} /></td>
                        <td>{r.vessel_name || '-'}</td>
                        <td>{r.port_name || '-'}</td>
                        <td>{r.position || '-'}</td>
                        <td style={{ maxWidth: 240, color: '#475569' }}>{r.note || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {showRecordModal && (
        <RecordModal
          crew={crew}
          vessels={vessels}
          ports={ports}
          onClose={() => setShowRecordModal(false)}
          onSaved={() => { setShowRecordModal(false); loadCurrent(); }}
        />
      )}
    </div>
  );
};

// ── 乗下船記録モーダル ───────────────────────────────────────────
function RecordModal({ crew, vessels, ports, onClose, onSaved }: {
  crew: CrewUser[]; vessels: Vessel[]; ports: Port[];
  onClose: () => void; onSaved: () => void;
}) {
  const [userId, setUserId] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [recordType, setRecordType] = useState<'board' | 'disembark'>('board');
  const [recordedAt, setRecordedAt] = useState('');
  const [portName, setPortName] = useState('');
  const [position, setPosition] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!userId) { alert('乗組員を選択してください'); return; }
    if (!vesselId) { alert('船舶を選択してください'); return; }
    setSaving(true);
    try {
      const body: any = { user_id: userId, vessel_id: vesselId, record_type: recordType };
      if (recordedAt) body.recorded_at = new Date(recordedAt).toISOString();
      if (portName) body.port_name = portName;
      if (position) body.position = position;
      if (note) body.note = note;
      await api.post('/boarding', body);
      onSaved();
    } catch (e: any) { alert(e.message); setSaving(false); }
  };

  const radioStyle = (active: boolean) => ({
    flex: 1, textAlign: 'center' as const, padding: '9px 0', borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: 700,
    border: `1px solid ${active ? '#2563eb' : '#e2e8f0'}`,
    background: active ? '#eff6ff' : '#fff',
    color: active ? '#2563eb' : '#64748b',
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">乗下船の記録</div>
        <div className="form-g">
          <label className="form-l">乗組員</label>
          <SearchableSelect
            value={userId}
            onChange={setUserId}
            options={crewVesselOptions(crew)}
            placeholder="氏名で検索..."
          />
        </div>
        <div className="form-g">
          <label className="form-l">船舶</label>
          <select className="form-i" value={vesselId} onChange={e => setVesselId(e.target.value)}>
            <option value="">選択してください</option>
            {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
        </div>
        <div className="form-g">
          <label className="form-l">区分</label>
          <div className="flex-row" style={{ gap: 8 }}>
            <div style={radioStyle(recordType === 'board')} onClick={() => setRecordType('board')}>乗船</div>
            <div style={radioStyle(recordType === 'disembark')} onClick={() => setRecordType('disembark')}>下船</div>
          </div>
        </div>
        <div className="form-g">
          <label className="form-l">発生日時</label>
          <input className="form-i" type="datetime-local" value={recordedAt} onChange={e => setRecordedAt(e.target.value)} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>未入力の場合は現在時刻で記録されます</div>
        </div>
        <div className="form-g">
          <label className="form-l">港</label>
          {ports.length > 0 ? (
            <select className="form-i" value={portName} onChange={e => setPortName(e.target.value)}>
              <option value="">選択してください</option>
              {ports.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          ) : (
            <input className="form-i" value={portName} onChange={e => setPortName(e.target.value)} placeholder="港名を入力" />
          )}
        </div>
        <div className="form-g">
          <label className="form-l">役職</label>
          <input className="form-i" value={position} onChange={e => setPosition(e.target.value)} placeholder="例: 船長 / 機関長" />
        </div>
        <div className="form-g">
          <label className="form-l">メモ</label>
          <textarea className="form-i" rows={2} value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <div className="flex-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn-o" onClick={onClose}>キャンセル</button>
          <button className="btn-p" onClick={submit} disabled={saving}>{saving ? '記録中…' : '記録'}</button>
        </div>
      </div>
    </div>
  );
}

export default BoardingPage;
